import React from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import type { TransitionProps } from '@/components/ui/system/props';
import { buildTransitionClasses } from '@/components/ui/system/transitions';
import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn('fixed inset-0 z-50 bg-black/50', className)}
      {...props}
    />
  );
}

const contentVariants = cva(
  'fixed top-[50%] left-[50%] z-50 flex flex-col w-full max-h-[calc(100vh-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-lg border bg-background shadow-lg sm:max-w-lg max-w-[calc(100%-2rem)]',
  {
    variants: {
      size: {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-lg',
        lg: 'sm:max-w-xl',
        xl: 'sm:max-w-2xl',
        '2xl': 'sm:max-w-4xl',
        '3xl': 'sm:max-w-5xl',
        '4xl': 'sm:max-w-7xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface DialogContentProps
  extends React.ComponentProps<typeof DialogPrimitive.Content>,
    TransitionProps,
    VariantProps<typeof contentVariants> {
  showCloseButton?: boolean;
  dismissible?: boolean;
  preventCloseAutoFocus?: boolean;
  /** When true, adds data-slot-inside to overlay so slot editing stays open when clicking overlay */
  slotInside?: boolean;
  /** Extra classes merged onto the inner content wrapper (the scrollable div). Useful for overriding overflow/layout per-dialog. */
  bodyClassName?: string;
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'md',
  transition = 'scale',
  transitionDurationClass,
  transitionEasingClass,
  slideDirection,
  dismissible = true,
  preventCloseAutoFocus = false,
  slotInside = false,
  onCloseAutoFocus,
  bodyClassName,
  ...props
}: DialogContentProps) {
  const transitions = buildTransitionClasses(transition, {
    durationClass: transitionDurationClass,
    easingClass: transitionEasingClass,
    slideDirection,
  });
  const preventIfNotDismissible = React.useCallback(
    (event: { preventDefault: () => void }) => {
      if (!dismissible) event.preventDefault();
    },
    [dismissible],
  );
  const handleCloseAutoFocus = React.useCallback(
    (event: Event) => {
      if (preventCloseAutoFocus) event.preventDefault();
      onCloseAutoFocus?.(event);
    },
    [preventCloseAutoFocus, onCloseAutoFocus],
  );
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay {...(slotInside && { 'data-slot-inside': true })} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(contentVariants({ size }), 'duration-200', transitions, className)}
        onInteractOutside={preventIfNotDismissible}
        onEscapeKeyDown={preventIfNotDismissible}
        onPointerDownOutside={preventIfNotDismissible}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        <div className={cn('relative flex-1 overflow-y-auto p-6 space-y-4', bodyClassName)}>
          {children}
        </div>
        {showCloseButton && (
          <DialogPrimitive.Close asChild>
            <Button
              data-slot="dialog-close"
              size="icon-sm"
              colorScheme="secondary"
              variant="ghost"
              className="absolute top-4 right-4 z-10"
              aria-label="Close"
            >
              <Icon icon={faXmark} />
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-1 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
